/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { useNavigate, Link } from 'react-router-dom';
import i18nContext from 'components/i18n-context';
import { NewContainer } from 'uikit/NewContainer/NewContainer';
import Button from 'uikit/Button/Button';
import Alert from 'uikit/Alert/Alert';
import { ROUTE_PATHS } from 'routes/constants';
import { getErrorMessageForAlert } from 'services/utils';
import CustomFileDragAndDrop from 'components/CustomFileDragAndDrop';
import { ReactComponent as ArrowUpIcon } from 'assets/arrow-up.svg';
import { ReactComponent as ArrowDownIcon } from 'assets/arrow-down.svg';
import CsvFormatTable from './components/CsvFormatTable';
import CsvValidationErrors from './components/CsvValidationErrors';
import './CsvImport.scss';

const CSV_TEMPLATE =
  'Sender Wallet,Amount,Currency,Comment,Payment Purpose,Account Number,Sort Code,Crypto Network,Bank Name,Bank Country,BIC,Beneficiary Type,Beneficiary First Name,Beneficiary Last Name,Beneficiary Company Name,Registration Number,Beneficiary Country,Beneficiary City,Beneficiary Post Code,Beneficiary Address\n12345678,100.00,EUR,Invoice payment,,DE89370400440532013000,,,,DE,COBADEFFXXX,INDIVIDUAL,John,Doe,,,,,,';

const parseCsvText = (text) => {
  const lines = text.split('\n').filter((line) => line.trim());
  if (lines.length < 2) return { headers: [], rows: [] };

  const parseRow = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (const char of line) {
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const headers = parseRow(lines[0]);
  const rows = lines.slice(1).map(parseRow);
  return { headers, rows };
};

const CsvImport = ({ customerNumber, paymentStore }) => {
  const i18n = useContext(i18nContext);
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [parsedCsvData, setParsedCsvData] = useState(null);
  const [isFormatExpanded, setIsFormatExpanded] = useState(false);
  const errorsRef = useRef(null);

  useEffect(() => {
    return () => {
      paymentStore.resetCsvImportState();
    };
  }, []);

  useEffect(() => {
    if (paymentStore.csvImportValidationErrors || paymentStore.csvImportError) {
      errorsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [paymentStore.csvImportValidationErrors, paymentStore.csvImportError]);

  const handleFileChange = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setSelectedFile(file);
      setFileError(null);
      setParsedCsvData(null);
      paymentStore.resetCsvImportState();

      const reader = new FileReader();
      reader.onload = (e) => {
        const parsed = parseCsvText(e.target.result);
        setParsedCsvData(parsed);
      };
      reader.readAsText(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError(null);
    setParsedCsvData(null);
    paymentStore.resetCsvImportState();
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    const result = await paymentStore.importCsv(customerNumber, selectedFile);

    if (result.success) {
      navigate(ROUTE_PATHS.UNAPPROVED_PAYMENTS);
    }
  };

  const handleDownloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'import-template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const filesList = selectedFile
    ? [{ id: 'csv-file', name: selectedFile.name, size: selectedFile.size, originalFile: selectedFile }]
    : [];

  return (
    <section className={'page-container'}>
      <NewContainer className={'csv-import'} header={i18n.getMessage('csvImport.title')}>
        <p className={'csv-import-description'}>
          {i18n.getMessage('csvImport.description.before')}
          <Link to={ROUTE_PATHS.UNAPPROVED_PAYMENTS}>{i18n.getMessage('csvImport.description.link')}</Link>
          {i18n.getMessage('csvImport.description.after')}
        </p>

        <div className={'csv-import-upload-section'}>
          <CustomFileDragAndDrop
            files={filesList}
            onChange={handleFileChange}
            onRemoveFiles={handleRemoveFile}
            onError={(errorCode) => setFileError(errorCode)}
            accept={'.csv'}
            multiple={false}
            maxSize={15 * 1024 * 1024}
            isDisabled={paymentStore.isCsvImporting}
            error={fileError}
            dropzoneLabel={i18n.getMessage('csvImport.dropzone.label')}
            fileTypesLabel={i18n.getMessage('csvImport.dropzone.fileTypes')}
          />
          <Button
            type={'primary'}
            size={'large'}
            onClick={handleUpload}
            isDisabled={!selectedFile || paymentStore.isCsvImporting}
            isLoading={paymentStore.isCsvImporting}
          >
            {i18n.getMessage('csvImport.button.upload')}
          </Button>
        </div>

        {paymentStore.csvImportError && (
          <div ref={errorsRef}>
            <Alert type={'error'} message={getErrorMessageForAlert(i18n, paymentStore.csvImportError)} />
          </div>
        )}

        {paymentStore.csvImportValidationErrors && (
          <CsvValidationErrors
            ref={errorsRef}
            errors={paymentStore.csvImportValidationErrors}
            csvData={parsedCsvData}
          />
        )}

        <p className={'csv-import-template'}>
          {i18n.getMessage('csvImport.downloadTemplate.before')}
          <button className={'csv-import-template-link'} type={'button'} onClick={handleDownloadTemplate}>
            {i18n.getMessage('csvImport.downloadTemplate.link')}
          </button>
          {i18n.getMessage('csvImport.downloadTemplate.after')}
        </p>

        <div className={'csv-import-section'}>
          <button
            className={'csv-import-section-toggle'}
            type={'button'}
            onClick={() => setIsFormatExpanded(!isFormatExpanded)}
          >
            {i18n.getMessage('csvImport.formatReference.toggle')}
            {isFormatExpanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
          </button>
          <div className={`csv-import-section-content ${isFormatExpanded ? 'expanded' : ''}`}>
            <div>
              <p className={'csv-import-section-description'}>
                {i18n.getMessage('csvImport.formatReference.description')}
              </p>
              <CsvFormatTable />
            </div>
          </div>
        </div>
      </NewContainer>
    </section>
  );
};

CsvImport.propTypes = {
  customerNumber: PropTypes.string,
  paymentStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  customerNumber: stores.userStore.userData.account?.account_number,
  paymentStore: stores.paymentStore
}))(observer(CsvImport));
